import type {
  ApiErrorShape,
  AuthProvider,
  CloudSite,
  ProjectRepository,
  Session,
  SiteProject,
  SiteRevision,
  PublishResult,
  PublishStatus,
  SaveProjectRequest,
  Workspace,
  CmsCollection,
  CmsRecord,
  CmsRecordPage,
  FormDefinition,
  FormSubmission,
  Automation,
  AutomationExecution,
  AssetMetadata,
  CollaborationInvite,
  WorkspaceMembership,
  WorkspaceNotification,
  PagePermission,
  ReviewComment,
  AuditEvent,
} from './contracts';

export class CloudApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly details: ApiErrorShape,
  ) {
    super(details.message);
  }
}

export class WebKilnApiClient implements ProjectRepository, AuthProvider {
  private readonly baseUrl: string;
  private readonly sameOrigin: boolean;
  constructor(baseUrl = import.meta.env.VITE_WEBKILN_API_URL ?? '') {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.sameOrigin =
      import.meta.env.VITE_WEBKILN_CLOUD_MODE === 'true' ||
      (typeof window !== 'undefined' && (!baseUrl || baseUrl === window.location.origin));
  }
  get configured(): boolean {
    return Boolean(this.baseUrl) || this.sameOrigin;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    if (!this.configured) throw new Error('CLOUD_BACKEND_NOT_CONFIGURED');
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const source = payload?.error ?? payload;
      const details: ApiErrorShape = {
        code: typeof source?.code === 'string' ? source.code : 'INTERNAL_FAILURE',
        message:
          typeof source?.message === 'string' && source.message
            ? source.message
            : `Request failed (${response.status}). Please try again.`,
      };
      throw new CloudApiError(response.status, details);
    }
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }
  private normalizeSession(response: BetterAuthSessionResponse | null): Session | null {
    if (!response?.user || !response.session) return null;
    return {
      user: {
        id: response.user.id,
        email: response.user.email,
        displayName: response.user.name,
        status: 'active',
      },
      expiresAt: String(response.session.expiresAt),
    };
  }
  async getSession(): Promise<Session | null> {
    return this.normalizeSession(
      await this.request<BetterAuthSessionResponse | null>('/api/auth/get-session'),
    );
  }
  async signUp(name: string, email: string, password: string): Promise<Session> {
    return this.normalizeSession(
      await this.request<BetterAuthSessionResponse>('/api/auth/sign-up/email', {
        method: 'POST',
        body: JSON.stringify({ name, email, password }),
      }),
    ) as Session;
  }
  async signIn(email: string, password: string): Promise<Session> {
    return this.normalizeSession(
      await this.request<BetterAuthSessionResponse>('/api/auth/sign-in/email', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    ) as Session;
  }
  signOut(): Promise<void> {
    return this.request('/api/auth/sign-out', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }
  requestPasswordReset(email: string): Promise<void> {
    return this.request('/api/auth/password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
  listWorkspaces(): Promise<Workspace[]> {
    return this.request('/api/workspaces');
  }
  createWorkspace(name: string): Promise<Workspace> {
    return this.request('/api/workspaces', { method: 'POST', body: JSON.stringify({ name }) });
  }
  listSites(workspaceId: string): Promise<CloudSite[]> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/sites`);
  }
  createSite(workspaceId: string, name: string, options?: { blank?: boolean }): Promise<CloudSite> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/sites`, {
      method: 'POST',
      body: JSON.stringify({ name, blank: options?.blank }),
    });
  }
  updateSite(
    siteId: string,
    update: { name?: string; status?: 'active' | 'archived'; customDomain?: string | null },
  ): Promise<void> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
  }
  duplicateSite(siteId: string, name?: string): Promise<CloudSite> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }
  deleteSite(siteId: string): Promise<void> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}`, { method: 'DELETE' });
  }
  listCollections(workspaceId: string): Promise<CmsCollection[]> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/collections`);
  }
  createCollection(
    workspaceId: string,
    input: { name: string; fields: unknown[] },
  ): Promise<CmsCollection> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/collections`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
  listRecords(collectionId: string, query = ''): Promise<CmsRecordPage> {
    return this.request(
      `/api/collections/${encodeURIComponent(collectionId)}/records${query ? `?${query}` : ''}`,
    );
  }
  createRecord(
    collectionId: string,
    data: Record<string, unknown>,
    status: 'draft' | 'published' = 'draft',
  ): Promise<CmsRecord> {
    return this.request(`/api/collections/${encodeURIComponent(collectionId)}/records`, {
      method: 'POST',
      body: JSON.stringify({ data, status }),
    });
  }
  updateRecord(
    recordId: string,
    data: Record<string, unknown>,
    status?: 'draft' | 'published',
  ): Promise<CmsRecord> {
    return this.request(`/api/records/${encodeURIComponent(recordId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ data, ...(status ? { status } : {}) }),
    });
  }
  deleteRecord(recordId: string): Promise<void> {
    return this.request(`/api/records/${encodeURIComponent(recordId)}`, { method: 'DELETE' });
  }
  exportCollection(collectionId: string): Promise<string> {
    return fetch(`${this.baseUrl}/api/collections/${encodeURIComponent(collectionId)}/export`, {
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) throw new Error('Could not export collection');
      return response.text();
    });
  }
  importCollection(collectionId: string, csv: string): Promise<{ imported: number }> {
    return this.request(`/api/collections/${encodeURIComponent(collectionId)}/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/csv' },
      body: csv,
    });
  }
  listForms(siteId: string): Promise<FormDefinition[]> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/forms`);
  }
  createForm(siteId: string, name: string, fields: unknown[]): Promise<FormDefinition> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/forms`, {
      method: 'POST',
      body: JSON.stringify({ name, fields }),
    });
  }
  updateForm(
    formId: string,
    input: {
      name?: string;
      fields?: unknown[];
      settings?: Record<string, unknown>;
      status?: 'active' | 'archived';
    },
  ): Promise<FormDefinition> {
    return this.request(`/api/forms/${encodeURIComponent(formId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }
  listSubmissions(formId: string, query = ''): Promise<FormSubmission[]> {
    return this.request(
      `/api/forms/${encodeURIComponent(formId)}/submissions${query ? `?${query}` : ''}`,
    );
  }
  updateSubmission(formId: string, submissionId: string, status: string): Promise<FormSubmission> {
    return this.request(
      `/api/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    );
  }
  deleteSubmission(formId: string, submissionId: string): Promise<void> {
    return this.request(
      `/api/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}`,
      { method: 'DELETE' },
    );
  }
  exportSubmissions(formId: string): Promise<string> {
    return fetch(`${this.baseUrl}/api/forms/${encodeURIComponent(formId)}/submissions/export`, {
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) throw new Error('Could not export submissions');
      return response.text();
    });
  }
  listAutomations(workspaceId: string): Promise<Automation[]> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/automations`);
  }
  createAutomation(
    workspaceId: string,
    input: { name: string; triggerType: string; graph: unknown },
  ): Promise<Automation> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/automations`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
  updateAutomation(automationId: string, update: Record<string, unknown>): Promise<Automation> {
    return this.request(`/api/automations/${encodeURIComponent(automationId)}`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
  }
  listAutomationExecutions(automationId: string): Promise<AutomationExecution[]> {
    return this.request(`/api/automations/${encodeURIComponent(automationId)}/executions`);
  }
  listAssets(workspaceId: string, query = ''): Promise<AssetMetadata[]> {
    return this.request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/assets${query ? `?${query}` : ''}`,
    );
  }
  createAsset(workspaceId: string, input: Record<string, unknown>): Promise<AssetMetadata> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/assets`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
  updateAsset(assetId: string, input: Record<string, unknown>): Promise<AssetMetadata> {
    return this.request(`/api/assets/${encodeURIComponent(assetId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }
  deleteAsset(assetId: string): Promise<void> {
    return this.request(`/api/assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' });
  }
  replaceAsset(
    assetId: string,
    replacementAssetId: string,
  ): Promise<{ replacedPages: number; replacementAssetId: string }> {
    return this.request(`/api/assets/${encodeURIComponent(assetId)}/replace`, {
      method: 'POST',
      body: JSON.stringify({ replacementAssetId }),
    });
  }
  listMembers(
    workspaceId: string,
  ): Promise<{ members: WorkspaceMembership[]; invitations: CollaborationInvite[] }> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/members`);
  }
  inviteMember(workspaceId: string, email: string, role: string): Promise<CollaborationInvite> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }
  updateInvitationRole(
    workspaceId: string,
    inviteId: string,
    role: string,
  ): Promise<{ id: string; role: string }> {
    return this.request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(inviteId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    );
  }
  cancelInvitation(workspaceId: string, inviteId: string): Promise<{ success: boolean }> {
    return this.request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    );
  }
  revealInvitationLink(
    workspaceId: string,
    inviteId: string,
  ): Promise<{ inviteUrl: string; expiresAt: string }> {
    return this.request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(inviteId)}/link`,
      { method: 'POST' },
    );
  }
  updateMemberRole(
    workspaceId: string,
    membershipId: string,
    role: string,
  ): Promise<WorkspaceMembership> {
    return this.request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(membershipId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    );
  }
  removeMember(workspaceId: string, membershipId: string): Promise<void> {
    return this.request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(membershipId)}`,
      { method: 'DELETE' },
    );
  }
  createReviewLink(
    siteId: string,
    mode: 'review' | 'client' = 'review',
  ): Promise<{ url: string; expiresAt: string; mode: string }> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/review-links`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    });
  }
  listReviewComments(siteId: string): Promise<ReviewComment[]> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/review-comments`);
  }
  createReviewComment(
    siteId: string,
    data: {
      pageId?: string;
      componentId?: string;
      body: string;
      mentions?: Array<{ userId: string; label?: string }>;
    },
  ): Promise<ReviewComment> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/review-comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  updateReviewCommentStatus(
    commentId: string,
    status: 'open' | 'resolved',
  ): Promise<ReviewComment> {
    return this.request(`/api/review-comments/${encodeURIComponent(commentId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }
  getReviewLinkProject(token: string): Promise<SiteProject & { mode?: string }> {
    return this.request(`/api/review-links/${encodeURIComponent(token)}/project`);
  }
  saveReviewLinkProject(token: string, request: SaveProjectRequest): Promise<SiteProject> {
    return this.request(`/api/review-links/${encodeURIComponent(token)}/project`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }
  getProject(siteId: string): Promise<SiteProject> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/project`);
  }
  saveProject(siteId: string, request: SaveProjectRequest): Promise<SiteProject> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/project`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }
  listRevisions(siteId: string): Promise<SiteRevision[]> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/revisions`);
  }
  createRevision(siteId: string, name: string, expectedRevision: number): Promise<SiteRevision> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/revisions`, {
      method: 'POST',
      body: JSON.stringify({ name, expectedRevision }),
    });
  }
  restoreRevision(
    siteId: string,
    revisionId: string,
    expectedRevision: number,
  ): Promise<SiteProject> {
    return this.request(
      `/api/sites/${encodeURIComponent(siteId)}/revisions/${encodeURIComponent(revisionId)}/restore`,
      { method: 'POST', body: JSON.stringify({ expectedRevision }) },
    );
  }
  listPagePermissions(siteId: string): Promise<PagePermission[]> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/page-permissions`);
  }
  setPagePermission(
    siteId: string,
    pageId: string,
    userId: string,
    role: string,
  ): Promise<{ pageId: string; userId: string; role: string }> {
    return this.request(
      `/api/sites/${encodeURIComponent(siteId)}/pages/${encodeURIComponent(pageId)}/permissions`,
      {
        method: 'PUT',
        body: JSON.stringify({ userId, role }),
      },
    );
  }
  deletePagePermission(siteId: string, pageId: string, userId: string): Promise<void> {
    return this.request(
      `/api/sites/${encodeURIComponent(siteId)}/pages/${encodeURIComponent(pageId)}/permissions/${encodeURIComponent(userId)}`,
      {
        method: 'DELETE',
      },
    );
  }
  getPublishStatus(siteId: string): Promise<PublishStatus> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/publish-status`);
  }
  publishSite(
    siteId: string,
    expectedRevision: number,
    password?: string,
    visibility?: 'public' | 'private',
  ): Promise<PublishResult> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/publish`, {
      method: 'POST',
      body: JSON.stringify({
        expectedRevision,
        ...(password ? { password } : {}),
        ...(visibility ? { visibility } : {}),
      }),
    });
  }
  unpublishSite(siteId: string): Promise<PublishResult> {
    return this.request(`/api/sites/${encodeURIComponent(siteId)}/unpublish`, { method: 'POST' });
  }
  listNotifications(workspaceId: string): Promise<WorkspaceNotification[]> {
    return this.request(`/api/workspaces/${encodeURIComponent(workspaceId)}/notifications`);
  }
  markNotificationRead(notificationId: string): Promise<WorkspaceNotification> {
    return this.request(`/api/notifications/${encodeURIComponent(notificationId)}/read`, {
      method: 'PATCH',
    });
  }
  deleteNotification(notificationId: string): Promise<void> {
    return this.request(`/api/notifications/${encodeURIComponent(notificationId)}`, {
      method: 'DELETE',
    });
  }
  rollbackPublishedSite(siteId: string, releaseId: string): Promise<PublishResult> {
    return this.request(
      `/api/sites/${encodeURIComponent(siteId)}/publish/${encodeURIComponent(releaseId)}/rollback`,
      { method: 'POST' },
    );
  }
  transferWorkspaceOwnership(
    workspaceId: string,
    targetMembershipId: string,
  ): Promise<{ success: boolean; newOwnerUserId: string }> {
    return this.request<{ success: boolean; newOwnerUserId: string }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/transfer-ownership`,
      {
        method: 'POST',
        body: JSON.stringify({ targetMembershipId }),
      },
    );
  }
  listAuditLog(workspaceId: string): Promise<AuditEvent[]> {
    return this.request<AuditEvent[]>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/audit-log`,
    );
  }
}

interface BetterAuthSessionResponse {
  user?: { id: string; email: string; name: string };
  session?: { expiresAt: string | Date };
}
